import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { STORE_API, type GameStore } from '@chess/api-model';
import { REVIEW_API, type ReviewTabs, type ReviewWorkspace } from '@chess/api-review';
import { CONSTRAINTS_API, type ConstraintOwner, type ConstraintRegistry } from '@chess/api-constraints';
import { BoardTabs } from './BoardTabs.tsx';
import type { TabRecord } from './labels.ts';
import { itemsOf, recordsToLoad, tabScope } from './items.ts';
import './board-tabs.css';

// The board tabs above the board (`review.boardTabs`): the game the address
// names, pinned first, and the games opened beside it to compare (the
// opening history opens them). The tabs and which one shows are the review
// workspace's; switching a tab switches every panel, since the `review` API
// stands in for the active tab. Labels come from each game's record, read
// once per game. A tab whose board is constrained (a puzzle) reads the
// registration's label instead, from the constraint registry: each tab is
// its own scope.

const SLOT = 'review.boardTabs';

export function setup(app: PiletApi) {
  const Strip: React.FC = () => {
    const [workspace, setWorkspace] = React.useState<ReviewWorkspace>();
    const [tabs, setTabs] = React.useState<ReviewTabs>();
    // Per game: its record's date and opponent words, read once.
    const [records, setRecords] = React.useState<Record<string, TabRecord>>({});
    const [games, setGames] = React.useState<GameStore>();
    // Per tab: who constrains its board, if anyone.
    const [registry, setRegistry] = React.useState<ConstraintRegistry>();
    const [owners, setOwners] = React.useState<Record<string, ConstraintOwner | undefined>>({});
    React.useEffect(() => {
      void app.getApi<ConstraintRegistry>(CONSTRAINTS_API).then(setRegistry);
    }, []);
    const tabIds = tabs?.tabs.map((t) => t.id).join(' ') ?? '';
    React.useEffect(() => {
      if (!registry || !tabIds) return;
      const offs = tabIds.split(' ').map((id) => {
        const scope = tabScope(id);
        setOwners((all) => ({ ...all, [id]: registry.owner(scope) }));
        return registry.subscribeOwner(scope, (owner) => setOwners((all) => ({ ...all, [id]: owner })));
      });
      return () => offs.forEach((off) => off());
    }, [registry, tabIds]);

    React.useEffect(() => {
      void app.getApi<ReviewWorkspace>(REVIEW_API).then((w) => {
        setWorkspace(w);
        setTabs(w.tabs?.());
      });
      void app.getApi<GameStore>(STORE_API).then(setGames);
    }, []);
    React.useEffect(() => (workspace?.subscribeTabs ? workspace.subscribeTabs(setTabs) : undefined), [workspace]);

    React.useEffect(() => {
      if (!games || !tabs) return;
      for (const id of recordsToLoad(tabs, records)) {
        void games.record(id).then((record) => {
          if (record) setRecords((all) => ({ ...all, [id]: record }));
        });
      }
    }, [games, tabs, records]);

    if (!workspace || !tabs || !workspace.subscribeTabs) return null;
    const items = itemsOf(tabs, records, owners);
    return <BoardTabs tabs={items} activeId={tabs.activeId} onActivate={(id) => workspace.activate(id)} onClose={(id) => workspace.close(id)} />;
  };

  app.registerExtension(SLOT, Strip);
}
